// Phase 36 Batch U — Settings → Change Password section.
//
// Wired into Settings.jsx between <ConnectedAccountsCard /> and the
// Preferences section, per spec. Self-contained so the parent page
// stays slim.
//
// Google-only users (`auth_providers == ["google"]` and no email
// auth) see an explainer card instead of the form — same place,
// same look, just non-interactive.

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { Lock, Eye, EyeOff, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function ChangePasswordCard() {
  const { user } = useAuth();

  // Google-only = `auth_providers` is exactly ["google"] (no "email"
  // entry). These users have no password_hash on file, so the
  // backend would reject the form with a 400 — we render the
  // read-only explainer instead.
  const isGoogleOnly = useMemo(() => {
    const providers = user?.auth_providers || [];
    return providers.length > 0 && providers.includes("google") && !providers.includes("email");
  }, [user]);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const clientError = useMemo(() => {
    // Only surface client-side validation AFTER the user has typed
    // something in confirm — avoids "Passwords don't match" flashing
    // while they're still typing.
    if (!confirmPw) return null;
    if (newPw.length < 8) return "New password must be at least 8 characters.";
    if (newPw !== confirmPw) return "New password and confirmation don't match.";
    if (newPw === currentPw) return "New password must be different from the current one.";
    return null;
  }, [currentPw, newPw, confirmPw]);

  const submit = async (e) => {
    e.preventDefault();
    setInlineError(null);
    if (clientError) {
      setInlineError(clientError);
      return;
    }
    if (!currentPw || newPw.length < 8 || newPw !== confirmPw) {
      setInlineError("Please fill in all three fields. New password needs 8+ characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/me/password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated. Your other sessions will be logged out.");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Show the backend's message verbatim when it's a 400 (it's
      // user-actionable copy like the Google-only guard). For 401
      // ("Current password is incorrect") render the canonical msg.
      if (status === 401) {
        setInlineError("Current password is incorrect.");
      } else if (typeof detail === "string") {
        setInlineError(detail);
      } else {
        setInlineError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Google-only read-only state ────────────────────────────────────
  if (isGoogleOnly) {
    return (
      <section
        data-testid="change-password-section"
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <header className="mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Lock className="h-4 w-4 text-indigo-600" /> Change password
          </h2>
        </header>
        <div
          data-testid="change-password-google-only"
          className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 text-sm text-slate-700"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-indigo-600" />
          <div>
            <p className="font-medium text-slate-900">You signed in with Google.</p>
            <p className="mt-1 text-slate-600">
              This account doesn't have a password yet. If you'd like to also
              have password access (handy if you ever lose access to your
              Google account), use{" "}
              <span className="font-medium text-slate-900">Forgot password</span>{" "}
              on the login page to set an initial one.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // ── Standard email/password user → full form ───────────────────────
  return (
    <section
      data-testid="change-password-section"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <header className="mb-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <Lock className="h-4 w-4 text-indigo-600" /> Change password
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          We'll log out all your other sessions when the password changes.
        </p>
      </header>

      <form onSubmit={submit} className="grid gap-5 sm:max-w-md">
        {/* Current password */}
        <div>
          <Label htmlFor="cp-current" className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Current password
          </Label>
          <div className="relative mt-1.5">
            <Input
              id="cp-current"
              type={showCurrent ? "text" : "password"}
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
              data-testid="settings-change-password-current"
              className="pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              aria-label={showCurrent ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <Label htmlFor="cp-new" className="text-xs font-medium uppercase tracking-wider text-slate-500">
            New password
          </Label>
          <div className="relative mt-1.5">
            <Input
              id="cp-new"
              type={showNew ? "text" : "password"}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              data-testid="settings-change-password-new"
              className="pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              aria-label={showNew ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">At least 8 characters.</p>
        </div>

        {/* Confirm */}
        <div>
          <Label htmlFor="cp-confirm" className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Confirm new password
          </Label>
          <Input
            id="cp-confirm"
            type={showNew ? "text" : "password"}
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            data-testid="settings-change-password-confirm"
            className="mt-1.5"
            required
          />
        </div>

        {/* Inline error (server or client) */}
        {(inlineError || clientError) && (
          <div
            data-testid="change-password-error"
            className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <span>{inlineError || clientError}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting || !!clientError || !currentPw || !newPw || !confirmPw}
          className="w-full bg-indigo-600 text-white hover:bg-indigo-700 sm:w-auto"
          data-testid="settings-change-password-submit"
        >
          {submitting ? "Updating…" : "Update password"}
        </Button>
      </form>
    </section>
  );
}
